// ProjectContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import useFetch from '@/hooks/useFetch';
import { ProjectProps } from '@/@types/common';
import { useAuthContext } from './AuthContext';

interface ProjectContextProps {
  projectData: ProjectProps[] | null;
  concrete_providers: boolean;
  loading: boolean;
  error: any;
}

const ProjectContext = createContext<ProjectContextProps>({
  projectData: null,
  concrete_providers: false,
  loading: false,
  error: null,
});

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authenticated } = useAuthContext();
  const { data, loading, error, execute } = useFetch<any>({
    url: '/projects/',
    method: 'GET',
    immediate: false,
  });

  const [projectData, setProjectData] = useState<ProjectProps[] | null>(null);
  const [concrete_providers, setConcreteProviders] = useState<boolean>(false);

  useEffect(() => {
    if (authenticated && !projectData) {
      execute();
    }
    if (!authenticated) {
      setProjectData(null);
      setConcreteProviders(false);
    }
  }, [authenticated]);

  useEffect(() => {
    if (error) {
      console.error('Error fetching projects:', error);
    }
  }, [error]);

  useEffect(() => {
    if (authenticated && data) {
      const projectData = data.data;
      const concrete_provider = data.concrete_providers;
      
      setProjectData(Array.isArray(projectData) ? projectData : null);
      setConcreteProviders(!!concrete_provider);
    }
  }, [data, authenticated]);

  if (!authenticated) {
    return (
      <ProjectContext.Provider
        value={{
          projectData: null,
          concrete_providers: false,
          loading: false,
          error: null
        }}
      >
        {children}
      </ProjectContext.Provider>
    );
  }

  return (
    <ProjectContext.Provider
      value={{
        projectData,
        concrete_providers,
        loading: authenticated ? loading : false,
        error: authenticated ? error : null
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useProjects must be used within a ProjectProvider");
  }
  return context;
};
